import { useGetProactiveChats } from "@/api/pro-active-chat/get-all-proactive-chats";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import ChatPreview from "../chat-preview/chat-preview";
import { AlertCircle, Delete } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useUpdateProactiveChat } from "@/api/pro-active-chat/update-proactive-chat";
import { useDeleteProactiveChat } from "@/api/pro-active-chat/delete-proactive-chat";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import NoDataFound from "@/components/layout/no-data-found";
import PaginationCombo from "@/components/ui/pagination-combo";
import { useSearchParams } from "react-router";
import useGlobalPermission from "@/store/user-permissions";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

type ChatType = {
    id: string;
    title: string;
    message: string;
    delay: number;
    scrawl_percentage: number;
    url: string;
    is_active: boolean;
};

const ProactiveChatList = () => {
    const [searchParams] = useSearchParams();
    const {
        data: proactiveChats,
        isLoading,
        isError,
    } = useGetProactiveChats({
        skip: (parseInt(searchParams.get("page") || "1") - 1) * parseInt(searchParams.get("per_page") || "10"),
        limit: parseInt(searchParams.get("per_page") || "10"),
    });
    const { mutate, isPending } = useUpdateProactiveChat();
    const { mutate: deleteChat } = useDeleteProactiveChat();

    const handleToggleStatus = (value: boolean, chat: ChatType) => {
        mutate({
            id: chat.id,
            is_active: value,
            title: chat.title,
            message: chat.message,
            delay: chat.delay,
            scrawl_percentage: chat.scrawl_percentage,
            url: chat.url,
        });
    };

    const { permissionList } = useGlobalPermission();
    const proactivePermission = permissionList.find((item) => item.module.name === "Proactive_Chat");

    return (
        <div className="relative">
            {!proactivePermission?.can_get_list && (
                <Alert variant="destructive" className="absolute z-10 top-1/2 right-1/2 translate-x-1/2 -translate-y-1/2 max-w-2xl">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>You do not have permission to view this list!</AlertDescription>
                </Alert>
            )}
            <Card className={cn("p-4", !proactivePermission?.can_get_list && "blur-md")}>
                <div className="max-w-[calc(100vw-100px)] overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Title</TableHead>
                                <TableHead>Message</TableHead>
                                <TableHead>URL</TableHead>
                                <TableHead>Message Delay (seconds)</TableHead>
                                <TableHead>Scroll Percentage (%)</TableHead>
                                <TableHead>Preview</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {!isLoading &&
                                !isPending &&
                                proactiveChats?.data?.map((chat: ChatType) => (
                                    <TableRow key={chat.id}>
                                        <TableCell>
                                            {!proactivePermission?.can_get_list
                                                ? "No permission to view"
                                                : chat.title || <p className="text-red-700 font-bold">Not Provided</p>}
                                        </TableCell>
                                        <TableCell>
                                            {!proactivePermission?.can_get_list
                                                ? "No permission to view"
                                                : chat.message || <p className="text-red-700 font-bold">Not Provided</p>}
                                        </TableCell>
                                        <TableCell>
                                            {!proactivePermission?.can_get_list
                                                ? "No permission to view"
                                                : chat.url || <p className="text-green-700 font-bold">ALL</p>}
                                        </TableCell>
                                        <TableCell>{!proactivePermission?.can_get_list ? "No permission to view" : chat.delay || 0}</TableCell>
                                        <TableCell>
                                            {!proactivePermission?.can_get_list ? "No permission to view" : chat.scrawl_percentage || 0}
                                        </TableCell>
                                        <TableCell>
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            size={"sm"}
                                                            variant={"link"}
                                                            disabled={!proactivePermission?.can_get_list || !proactivePermission?.can_get_one}
                                                        >
                                                            Hover
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <ChatPreview title={chat.title} message={chat.message} />
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={chat.is_active}
                                                onCheckedChange={(value) => handleToggleStatus(value, chat)}
                                                disabled={!proactivePermission?.can_update}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        className="cursor-pointer"
                                                        size={"sm"}
                                                        variant={"destructive"}
                                                        disabled={!proactivePermission?.can_delete}
                                                    >
                                                        <Delete /> Delete
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            This action cannot be undone. This will permanently delete the account and remove the data
                                                            from our servers.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                        <AlertDialogAction onClick={() => deleteChat({ id: chat.id })}>Continue</AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {(isLoading || isPending) &&
                                [1, 2, 3, 4, 5, 6, 7, 8, 9].map((item) => (
                                    <TableRow key={item}>
                                        <TableCell className="p-4 font-medium">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <Skeleton className="h-4 w-full" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {(isError || !proactiveChats?.data) && !isLoading && (
                                <TableRow>
                                    <TableCell colSpan={8} className="w-full text-center">
                                        <NoDataFound message="No Leads Found" />
                                    </TableCell>
                                </TableRow>
                            )}
                            {proactiveChats?.data?.length === 0 && !isLoading && (
                                <TableRow>
                                    <TableCell colSpan={8} className="w-full text-center">
                                        <NoDataFound message="No Leads Found" />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <PaginationCombo totalItems={proactiveChats?.pagination?.total_entity} />
            </Card>
        </div>
    );
};

export default ProactiveChatList;
